import { IonButton, IonCheckbox, IonIcon, IonPopover } from '@ionic/react';
import {
  caretForwardOutline,
  checkmarkCircleOutline,
  closeOutline,
  copyOutline,
  folderOpenOutline,
} from 'ionicons/icons';
import React, { useState } from 'react';
import { run } from '../../../helper/electron';
import { useIcons } from '../../../hooks/useIcons';
import { copyToClipboard } from '../../../utils/copyToClipboard';
import { useToast } from '../../ToastManager/useToast';
import './RekordboxExportPopover.scss';

interface RekordboxGuidePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  path: string;
}

const RekordboxGuidePopover: React.FC<RekordboxGuidePopoverProps> = ({
  isOpen,
  onClose,
  path,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { icons } = useIcons();
  const steps = [
    { id: 1, icon: icons['rekordbox'], title: 'Open Rekordbox' },
    {
      id: 2,
      icon: caretForwardOutline,
      title: 'Go to File → Import → Import Playlist',
    },
    {
      id: 3,
      icon: folderOpenOutline,
      title: 'Browse to the exported .m3u file',
    },
    {
      id: 4,
      icon: checkmarkCircleOutline,
      title: 'Click Open',
      description: 'The playlist will appear in your Rekordbox collection.',
    },
  ];
  const { successToast, errorToast } = useToast();
  const viewCount =
    JSON.parse(localStorage.getItem('hide-rekord-export-guide') ?? '{}')
      .count ?? 0;

  const openPath: any = () => {
    run('open-directory', {
      folderpath: path,
    });
  };

  const copyPath = async () => {
    const success = await copyToClipboard(path);
    if (success) {
      successToast({ message: 'Path copied' });
    } else {
      errorToast({ message: 'Failed to copy to clipboard' });
    }
  };

  const onDismiss = () => {
    localStorage.setItem(
      'hide-rekord-export-guide',
      JSON.stringify({
        count: viewCount + 1,
        hide: dontShowAgain,
      }),
    );

    onClose();
  };
  return (
    <IonPopover
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      backdropDismiss={false}
      className="rekordbox-guide-popover"
    >
      <div className="rekordbox-guide-container">
        <div className="rekordbox-guide-header">
          <div>
            <h3>Your Rekordbox playlist has been exported.</h3>
            <p>To import it into Rekordbox:</p>
          </div>

          <IonButton fill="clear" size="small" onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        </div>

        <div className="rekordbox-guide-steps">
          {steps.map(({ icon, title, description, id }) => (
            <div key={title} className="rekordbox-guide-step">
              <div className="step-number">{id}</div>

              <IonIcon icon={icon} className="step-icon" />

              <div className="step-content">
                <h4>{title}</h4>
                {id == 3 && (
                  <>
                    <p className="guide-path">[{path}]</p>

                    <IonButton
                      fill="clear"
                      className="guide-action-button"
                      onClick={copyPath}
                    >
                      <IonIcon
                        icon={copyOutline}
                        className="guide-action-icon"
                      />
                      <span>Copy Path</span>
                    </IonButton>

                    <IonButton
                      fill="clear"
                      className="guide-action-button"
                      onClick={openPath}
                    >
                      <IonIcon
                        icon={folderOpenOutline}
                        className="guide-action-icon"
                      />
                      <span>Open Path</span>
                    </IonButton>
                  </>
                )}
                <p>{description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="rekordbox-guide-footer">
          {viewCount > 5 && (
            <IonCheckbox
              checked={dontShowAgain}
              onIonChange={(e) => setDontShowAgain(e.detail.checked)}
              labelPlacement="end"
            >
              Don't show this again
            </IonCheckbox>
          )}

          <div className="rekordbox-guide-footer-buttons">
            <IonButton onClick={onDismiss}>Got it</IonButton>
          </div>
        </div>
      </div>
    </IonPopover>
  );
};

export default RekordboxGuidePopover;
